import { Component, type ErrorInfo, type ReactNode } from "react";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import { healCachedChunks } from "@/lib/chunkCacheHeal";
import { ErrorDetails } from "@/components/ErrorDetails";
import { AUTO_RELOAD_KEY } from "@shared/errorReport";

/**
 * A lazily-imported route chunk can 404 after a fresh deploy: the browser is
 * still holding the previous build's `index.html` (cached, or an already-open
 * tab), so it requests a chunk whose content hash no longer exists. `React.lazy`
 * then rejects, `<Suspense>` can't catch it, and the whole tree unmounts to a
 * blank page. A hard reload pulls the new `index.html` (new hashes) and fixes it.
 *
 * So: when we detect that failure mode we reload ONCE automatically. The
 * sessionStorage timestamp guards against an infinite reload loop when the chunk
 * is genuinely broken (not just stale) — if we reloaded moments ago and hit the
 * same error, we stop and show the manual fallback instead.
 */
const RELOAD_KEY = AUTO_RELOAD_KEY;
const RELOAD_WINDOW_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : "";
  return (
    name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /'?text\/html'? is not a valid JavaScript MIME type/i.test(message) ||
    // WebKit/Safari (incl. iOS) reject a failed module fetch with a bare
    // "Load failed" / "Unable to load" — no "module" hint like Chrome — so a
    // first-load chunk failure on iPhone slips past the patterns above and the
    // auto-reload never fires, stranding the buyer on a blank page.
    /Load failed/i.test(message) ||
    /Unable to load/i.test(message) ||
    // Stale deploy on iOS Safari: the cached app shell lazy-loads a route chunk
    // whose sibling dependency chunk came from a *different* build, so an import
    // resolves to a chunk that no longer exports that (minified) binding. Safari
    // reports "Importing binding name 'X' is not found." — a link-time failure,
    // not a fetch failure, so none of the patterns above catch it and the card
    // sticks forever. A single hard reload pulls a consistent build and heals it.
    /Importing binding name .* is not found/i.test(message)
  );
}

/**
 * True from the moment we ask the browser to reload until the new document
 * commits. `window.location.reload()` is asynchronous — the old document stays
 * on screen for a few hundred ms — so anything that paints in that window is a
 * flash the user sees for no reason.
 *
 * This has to be a module flag, NOT the sessionStorage timestamp: `loadChunk`
 * already spends the timestamp budget before it rethrows, so by the time the
 * error reaches the boundary `reloadOnceGuarded` declines and the boundary can
 * no longer tell "nothing is happening" from "a reload is already running".
 */
let reloadInFlight = false;

/** True while a reload we triggered is still in flight — hold the calm screen. */
export function isReloadInFlight(): boolean {
  return reloadInFlight;
}

/**
 * Reload the page once, guarded against an infinite loop by a sessionStorage
 * timestamp. Returns true if a reload was triggered, false if we already
 * reloaded within the guard window (the failure is genuine, not stale).
 * Pass the failure so a chunk the browser cached broken is refetched first.
 */
export function reloadOnceGuarded(error?: unknown): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked (private mode / no storage): reload anyway once.
  }
  reloadInFlight = true;
  void healCachedChunks(error).finally(() => window.location.reload());
  return true;
}

/**
 * Reload once for a stale-deploy chunk error. Returns true if a reload was
 * triggered (so callers can keep rendering the loader instead of the fallback),
 * false if we already reloaded recently and should give up.
 */
export function recoverFromChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  return reloadOnceGuarded(error);
}

/**
 * How long the calm spinner is held before the error card is allowed to paint.
 * Recovery (a retry, a reload, a redirect) either wins inside this window or is
 * not coming at all, so a crash that self-heals never gets to flash a scary card
 * at the user. The cost is that a genuine crash takes this much longer to
 * surface — invisible next to the reading time of the card itself.
 */
const CARD_GRACE_MS = 500;

/** When the error was caught, and which component threw — for the error report. */
export interface Caught {
  at: number;
  componentStack?: string;
}

export interface HoldState {
  error: Error | null;
  /** While true the boundary paints its calm screen instead of the error card. */
  holding: boolean;
  caught?: Caught;
}

/**
 * The anti-flash rules, shared by every boundary in the app so a new one can't
 * quietly reintroduce the bug. Subclasses only choose what "calm" and "failed"
 * look like at their level (full screen vs. inside the content area).
 */
export abstract class CalmErrorBoundary<P extends { children?: ReactNode }> extends Component<
  P,
  HoldState
> {
  state: HoldState = { error: null, holding: false };
  private holdTimer?: number;

  static getDerivedStateFromError(error: Error): Partial<HoldState> {
    // Hold on EVERY error, not just ones we can classify as a stale chunk: the
    // card must never win a race against a recovery that is already running.
    // componentDidCatch decides how long the hold lasts.
    return { error, holding: true, caught: { at: Date.now() } };
  }

  componentDidCatch(error: Error, info: ErrorInfo | null) {
    this.setState({
      caught: { at: this.state.caught?.at ?? Date.now(), componentStack: info?.componentStack ?? undefined },
    });
    // A reload is already running (loadChunk fired it before rethrowing, or an
    // earlier error did) — the document is about to be replaced, so hold the
    // calm screen until it is and never paint the card.
    if (isReloadInFlight()) return;
    // recoverFromChunkError reloads the page when it accepts; holding stays on.
    if (recoverFromChunkError(error)) return;
    // Nothing is recovering. Give the grace window, then show the card.
    this.holdTimer = window.setTimeout(() => this.setState({ holding: false }), CARD_GRACE_MS);
  }

  componentWillUnmount() {
    this.clearHold();
  }

  /** Drop a pending card reveal — for a subclass that clears the error itself. */
  protected clearHold() {
    if (this.holdTimer) window.clearTimeout(this.holdTimer);
    this.holdTimer = undefined;
  }

  protected abstract renderHolding(): ReactNode;
  protected abstract renderFallback(error: Error, caught: Caught): ReactNode;

  render() {
    const { error, holding, caught } = this.state;
    if (!error) return this.props.children;
    return holding ? this.renderHolding() : this.renderFallback(error, caught ?? { at: Date.now() });
  }
}

export class ErrorBoundary extends CalmErrorBoundary<{ children: ReactNode }> {
  protected renderHolding(): ReactNode {
    return <FullScreenSpinner />;
  }

  protected renderFallback(error: Error, caught: Caught): ReactNode {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Ada masalah memuatkan halaman</h1>
          <p className="mt-1 text-sm text-slate-500">
            Cuba muat semula. Kalau masih tak jadi, tutup dan buka semula pelayar anda.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void healCachedChunks(error).finally(() => window.location.reload())}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Muat semula
        </button>

        <ErrorDetails error={error} caught={caught} />
      </div>
    );
  }
}
